import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { OrderDirection, PostFilterResponse, PostState } from '../../Models/Models';
import { SiteContext } from '../../Contexts';
import { PostService } from '../../Services/PostService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faPaperclip, faLink } from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from '../Tooltips/CustomTooltip';
import DeleteButton from '../Buttons/DeleteButton';
import EditButton from '../Buttons/EditButton';
import { EntityColors } from '../../Themes/EntityColors';
import ReusableModal from '../Modals/ReusableModal';
import { Utils } from '../../Services/Utils';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import UserAvatarCell from '../Avatar/UserAvatarCell';
import LanguageBadgeList from '../Language/LanguageBadgeList';

const PostTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Príspevky');
    }, []);

    const { filterPosts, deletePost } = PostService();
    const [response, setResponse] = useState<PostFilterResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(6);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');
    const [stateFilter, setStateFilter] = useState<PostState | 'All'>('All');
    const { currentSite }: any = useContext(SiteContext);
    const { action, icon } = EntityColors.blog;

    const [showModal, setShowModal] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<number | null>(null);

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection; state?: PostState | 'All' }) => {
        const limit = opts?.perPage ?? perPage;
        const state = opts?.state ?? stateFilter;
        const resp = await filterPosts({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
            state: state === 'All' ? undefined : state,
        });
        setResponse(resp ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    const changeStateFilter = (state: PostState | 'All') => {
        setStateFilter(state);
        setPage(1);
        void loadPage(1, { state });
    };

    const confirmDelete = () => {
        if (toDeleteId !== null) {
            deletePost(toDeleteId).then(() => {
                setShowModal(false);
                setToDeleteId(null);
                loadPage(1);
                setPage(1);
            });
        }
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite]);

    return (
        <>
            <TableShell
                title="Príspevky"
                icon={icon}
                accentColor={action}
                createHref="/post/create"
                isLoading={response === null}
                isEmpty={response !== null && response.data.length === 0}
                emptyMessage="Žiadne príspevky"
                page={page}
                totalCount={response?.totalCount}
                perPage={perPage}
                onPageChange={p => { setPage(p); loadPage(p); }}
                toolbar={
                    <TableToolbar
                        totalCount={response?.totalCount ?? 0}
                        nounForms={['príspevok', 'príspevky', 'príspevkov']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                        filter={{
                            label: 'Stav',
                            value: stateFilter,
                            onChange: value => changeStateFilter(value as PostState | 'All'),
                            options: [
                                { value: 'All', label: 'Všetky' },
                                { value: 'Draft', label: 'Koncepty' },
                                { value: 'Published', label: 'Publikované' },
                            ],
                        }}
                    />
                }
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Nadpis</th>
                            <th>Popis</th>
                            <th>Jazyky</th>
                            <th>Stav</th>
                            <th>Vytvorené</th>
                            <th>Vytvoril</th>
                            <th>Naposledy upravené</th>
                            <th>Upravil</th>
                            <th>Odkaz</th>
                            {currentSite.settings.usesGallery && <th>Galéria</th>}
                            <th className="text-center">Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {response?.data.map(bp => (
                            <tr key={bp.id}>
                                <td className="fw-medium">{bp.headline}</td>
                                <td className="st-cell-soft">{Utils.truncateString(bp.description, 50)}</td>
                                <td>
                                    <LanguageBadgeList
                                        languages={bp.languages}
                                        accentColor={action}
                                        tooltipIdPrefix={`post-lang-${bp.id}`}
                                    />
                                </td>
                                <td>
                                    <CustomTooltip
                                        placement="top"
                                        tooltipId="post-state-tooltip"
                                        hideOn={bp.state !== 'Published' || !bp.publishedAt}
                                        message={bp.publishedAt ? Utils.formatDate(bp.publishedAt) : ''}
                                    >
                                        <span className={`st-pill ${bp.state === 'Published' ? 'st-pill--published' : 'st-pill--draft'}`}>
                                            {bp.state === 'Published' ? 'Publikované' : 'Koncept'}
                                        </span>
                                    </CustomTooltip>
                                </td>
                                <td className="text-nowrap">
                                    {Utils.formatDate(bp.createdAt)}
                                </td>
                                <td>
                                    <UserAvatarCell userId={bp.createdById} />
                                </td>
                                <td className="text-nowrap st-cell-soft">
                                    {bp.modifiedAt ? Utils.formatDate(bp.modifiedAt) : '—'}
                                </td>
                                <td>
                                    <UserAvatarCell userId={bp.modifiedById} />
                                </td>
                                <td>
                                    {bp.file ? (
                                        <CustomTooltip placement="top" message={bp.file.fileName} tooltipId="post-link-tooltip">
                                            <a
                                                href={bp.file.filePath}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="st-post-link"
                                                style={{ '--link-accent': action } as React.CSSProperties}
                                            >
                                                <FontAwesomeIcon icon={faPaperclip} className="me-1" />
                                                Súbor
                                            </a>
                                        </CustomTooltip>
                                    ) : bp.link ? (
                                        <CustomTooltip placement="top" message={bp.link} tooltipId="post-link-tooltip">
                                            <a
                                                href={bp.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="st-post-link"
                                                style={{ '--link-accent': action } as React.CSSProperties}
                                            >
                                                <FontAwesomeIcon icon={faLink} />
                                                Odkaz
                                            </a>
                                        </CustomTooltip>
                                    ) : (
                                        <span className="st-inline-flag st-inline-flag--none">
                                            <FontAwesomeIcon icon={faTimesCircle} />
                                            Žiaden
                                        </span>
                                    )}
                                </td>
                                {currentSite.settings.usesGallery && (
                                    <td>
                                        {bp.galleryName ? (
                                            <span className="st-inline-flag st-inline-flag--yes">
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                {bp.galleryName}
                                            </span>
                                        ) : (
                                            <span className="st-inline-flag st-inline-flag--none">
                                                <FontAwesomeIcon icon={faTimesCircle} />
                                                Nie
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="text-center">
                                    <div className="st-row-actions">
                                        <CustomTooltip placement="left" message="Upraviť">
                                            <Link to={`/post/edit/${bp.id}`}>
                                                <EditButton color={action} iconOnly />
                                            </Link>
                                        </CustomTooltip>
                                        <CustomTooltip placement="right" message="Zmazať">
                                            <DeleteButton
                                                color={action}
                                                iconOnly
                                                onClick={() => { setToDeleteId(bp.id); setShowModal(true); }}
                                            />
                                        </CustomTooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableShell>

            <ReusableModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={confirmDelete}
                title="Potvrdiť zmazanie"
                description="Naozaj chcete zmazať tento príspevok?"
            />
        </>
    );
};

export default PostTableComponent;
